import { useState, useRef } from 'react';
import { CarouselAutoplayControl as AriaAutoplayControl } from 'react-aria-carousel';
import { useFocusRing } from '@react-aria/focus';
import styles from './AutoplayControl.module.css';
import iconButtonStyles from '../../IconButton/IconButton.module.css';
import { Icon } from '../../Icon/Icon';
import { focusStyleVariants } from '../../../utils/focus/focusStyles';

export interface AutoplayControlProps {
  /**
   * Focus style
   * @default 'default'
   */
  focusStyle?: 'default' | 'white';

  /**
   * Style variant of the button
   * @default 'neutralVibrant'
   */
  variant?: 'neutralVibrant' | 'neutralSubtle' | 'whiteVibrant' | 'whiteSubtle';

  /**
   * Size of the button
   * @default 'md'
   */
  size?: 'sm' | 'md';

  /**
   * Additional CSS class name for the container
   */
  className?: string;

  /**
   * Additional CSS class name for the button
   */
  buttonClassName?: string;

  /**
   * Callback fired when the autoplay button is pressed
   */
  onPress?: (state: 'play' | 'pause') => void;
}

export const AutoplayControl = ({
  variant = 'neutralVibrant',
  size = 'md',
  focusStyle = 'default',
  className = '',
  buttonClassName = '',
  onPress,
}: AutoplayControlProps) => {
  const { isFocusVisible, focusProps, isFocused } = useFocusRing();
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const innerDivRef = useRef<HTMLDivElement>(null);

  // Generate CSS class based on variant
  const variantClass = styles[variant] || '';

  // Map button size to icon size:
  // - 'md' button uses '1.25x' icon
  // - 'sm' button uses '1x' icon
  const iconSize = size === 'md' ? '1.25x' : '1x';

  // Determine the icon color based on variant
  const getIconColor = () => {
    switch (variant) {
      case 'whiteVibrant':
        return 'whiteOnVibrant';
      case 'whiteSubtle':
        return 'whiteOnSubtle';
      case 'neutralVibrant':
        return 'inverseOnVibrant';
      case 'neutralSubtle':
        return 'inverseOnSubtle';
      default:
        return 'whiteOnVibrant';
    }
  };

  // Handle keyboard events for better accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent default behavior for space and enter to avoid double-triggering
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      setIsPressed(true);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    // Trigger the action on keyboard release
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      setIsPressed(false);

      // Programmatically click the inner div to trigger the carousel's autoplay toggle
      if (innerDivRef.current) {
        innerDivRef.current.click();
      }
    }
  };

  // Get title attribute for tooltip
  const getTitle = (autoplayUserPreference: boolean) => {
    return autoplayUserPreference ? 'Pause slideshow' : 'Start slideshow';
  };

  return (
    <AriaAutoplayControl
      className={`${styles.autoplayControl} ${styles[`size${size.toUpperCase()}`]} ${variantClass} ${className || ''}`}
      {...focusProps}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPressed(false); // Reset pressed state on mouse leave
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      data-hovered={isHovered ? true : undefined}
      data-pressed={isPressed ? true : undefined}
      data-focused={isFocused ? true : undefined}
      data-size={size}
      tabIndex={0} // Ensure it's in the tab order
      role="button"
    >
      {({ autoplayUserPreference }) => {
        return (
          <div
            ref={innerDivRef}
            className={`${styles.iconButton} ${iconButtonStyles.iconButton} ${iconButtonStyles[size] || ''} ${focusStyleVariants({ focusStyle })} ${buttonClassName || ''}`}
            data-focused={isFocused ? true : undefined}
            data-focus-visible={isFocusVisible ? true : undefined}
            data-parent-hovered={isHovered ? true : undefined}
            data-parent-pressed={isPressed ? true : undefined}
            data-size={size}
            aria-hidden="true" // Hide from screen readers since parent has the semantics
            title={getTitle(autoplayUserPreference)}
            onClick={() => {
              if (onPress) {
                // Pass the action being taken based on the current icon shown
                onPress(autoplayUserPreference ? 'pause' : 'play');
              }
            }}
          >
            <Icon
              iconPrefix="fas"
              iconName={autoplayUserPreference ? 'pause' : 'play'}
              color={getIconColor()}
              iconSize={iconSize}
              aria-hidden="true"
            />
          </div>
        );
      }}
    </AriaAutoplayControl>
  );
};

export default AutoplayControl;
